import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Drawer,
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Input, 
  Select, 
  Typography,
  Alert,
  Radio,
  Divider
} from 'antd';
import { 
  DeleteOutlined,
  UserOutlined
} from '@ant-design/icons';
import { FiEdit, FiTrash2, FiSave, FiX, FiEye } from 'react-icons/fi';
import { ResetIcon, RefreshIcon } from '../../common/ActionIcons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { 
  listSubjects, 
  listSubjectsByIds, 
  deleteSubject, 
  updateSubject, 
  loadSubjectDto 
} from '../../../actions/subjectActions';
import { listCohorts, listCohortsByIds } from '../../../actions/cohortActions';
import { listPatients, listPatientsByIds } from '../../../actions/patientActions';
import SingleEntitySelectMiniature from '../../crud/SingleEntitySelectMiniature.component';
import { showApiError } from '../../../utils/formatApiError';
import ApiErrorAlert from '../../../components/common/ApiErrorAlert';

const { Text } = Typography;

const SubjectTableTitle = ({
  filter,
  setFilter,
  searchText,
  setSearchText,
  searchProp,
  setSearchProp,
  selectedRowKeys,
  handleBulkDelete,
  isBulkDeleting,
  onRefresh,
  statusFilter,
  setStatusFilter,
  t,
  cohortFilter,
  setCohortFilter,
  patientFilter,
  setPatientFilter
}) => {
  const searchOptions = [
    { value: 'subjectIdentifier', label: t('Subject ID') },
    { value: 'patientId', label: t('Patient ID') },
    { value: 'postcode', label: t('Postcode') },
    { value: 'studyRole', label: t('Study Role') },
    { value: 'enrollmentStatus', label: t('Enrollment Status') },
    { value: 'approvalStatus', label: t('Approval Status') }
  ];

  const statusOptions = [
    { label: t('All'), value: 'all' },
    { label: t('Approved'), value: 'APPROVED' },
    { label: t('Rejected'), value: 'REJECTED' },
    { label: t('Pending'), value: 'PENDING' }
  ];

  const handleSearchPropChange = (value) => {
    setSearchProp(value);
    setFilter(prev => ({ ...prev, filter: value, query: searchText, page: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilter(prev => ({ ...prev, filter: searchProp, query: value, page: 1 }));
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setFilter(prev => ({ 
      ...prev, 
      filter: 'status',
      query: value === 'all' ? '' : value,
      page: 1 
    }));
  };

  const handleCohortFilterChange = (cohortId, cohort) => {
    setCohortFilter(cohortId);
    setPatientFilter(null); // Reset patient filter when cohort changes
    setFilter(prev => ({ 
      ...prev, 
      filter: 'cohortId',
      query: cohortId || '',
      page: 1 
    }));
  };

  const handlePatientFilterChange = (patientId, patient) => {
    setPatientFilter(patientId);
    setCohortFilter(null); // Reset cohort filter when patient changes
    setFilter(prev => ({ 
      ...prev, 
      filter: 'patientId',
      query: patientId || '',
      page: 1 
    }));
  };

  const handleReset = () => {
    setSearchText('');
    setSearchProp('subjectIdentifier');
    setStatusFilter('all');
    setCohortFilter(null);
    setPatientFilter(null);
    setFilter({
      query: '',
      filter: 'subjectIdentifier',
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Space wrap style={{ marginBottom: 12 }}>
        <span style={{ fontSize: '12px', color: '#666' }}>{t('Search')}:</span>
        <Select
          placeholder={t("Search by")}
          style={{ width: 140 }}
          size="small"
          value={searchProp}
          onChange={handleSearchPropChange}
        >
          {searchOptions.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
        <Input.Search
          placeholder={`${t("Search by")} ${searchOptions.find(opt => opt.value === searchProp)?.label || ''}`}
          onSearch={handleSearch}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 250 }}
          size="small"
        />
        
        {/* Cohort Filter */}
        <SingleEntitySelectMiniature
          singularEntity="cohort"
          pluralEntity="cohorts"
          listEntities={listCohorts}
          listEntitiesByIds={listCohortsByIds}
          value={cohortFilter}
          onChange={handleCohortFilterChange}
          primarySearchProp="name"
          secondarySearchProp="description"
          placeholder={t('Filter by cohort')}
          isMultiSelect={false}
          enableCreate={false}
          enableEditing={false}
        />

        {/* Patient Filter */}
        <SingleEntitySelectMiniature
          singularEntity="patient"
          pluralEntity="patients"
          listEntities={listPatients}
          listEntitiesByIds={listPatientsByIds}
          value={patientFilter}
          onChange={handlePatientFilterChange}
          primarySearchProp="patientIdentifier"
          secondarySearchProp="nhsNumber"
          placeholder={t('Filter by patient')}
          isMultiSelect={false}
          enableCreate={false}
          enableEditing={false}
        />
        
        <Button icon={<ResetIcon />} onClick={handleReset} size="small">
          {t("Reset")}
        </Button>
        <Button icon={<RefreshIcon />} onClick={onRefresh} size="small">
          {t('Refresh')}
        </Button>
      </Space>

      <div>
        <Text style={{ fontSize: '12px', color: '#666', marginRight: 8 }}>{t('Status')}:</Text>
        <Radio.Group
          value={statusFilter}
          onChange={handleStatusFilterChange}
          size="small"
          buttonStyle="solid"
        >
          {statusOptions.map((option) => (
            <Radio.Button key={option.value} value={option.value}>
              {option.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>
    </div>
  );
};

const SubjectListByLocation = ({ 
  visible, 
  onClose, 
  subjectIds = [], 
  title = "Subjects",
  uploadBatchId = null // If provided, shows subjects for this upload batch
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const subjectList = useSelector(state => state.subjectList);
  const { loading: loadingList, subjects = [], totalPages, currentPage, totalCount, error: errorList } = subjectList;

  const subjectListByIds = useSelector(state => state.subjectListByIds);
  const { loading: loadingListByIds, subjects: subjectsByIds = [], error: errorListByIds } = subjectListByIds;

  const subjectDelete = useSelector(state => state.subjectDelete);
  const { loading: loadingDelete, success: successDelete, error: errorDelete } = subjectDelete;

  const subjectUpdate = useSelector(state => state.subjectUpdate);
  const { loading: loadingUpdate, success: successUpdate, error: errorUpdate, subject: updatedSubject } = subjectUpdate;

  // Local state
  const [filter, setFilter] = useState({
    query: '',
    filter: 'subjectIdentifier',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchProp, setSearchProp] = useState('subjectIdentifier');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cohortFilter, setCohortFilter] = useState(null);
  const [patientFilter, setPatientFilter] = useState(null);
  const [localSubjects, setLocalSubjects] = useState([]);

  // Load subjects when drawer opens or filter changes
  useEffect(() => {
    if (visible) {
      if (uploadBatchId) {
        // For upload batch subjects, use listSubjectsByIds (no pagination needed)
        dispatch(listSubjectsByIds(subjectIds));
      } else {
        // For general subject list, use backend pagination and filtering
        dispatch(listSubjects(filter));
      }
    }
  }, [visible, subjectIds, uploadBatchId, filter, dispatch]);

  // Sync local state with Redux state
  useEffect(() => {
    if (uploadBatchId) {
      setLocalSubjects(subjectsByIds);
    } else {
      setLocalSubjects(subjects);
    }
  }, [subjects, subjectsByIds, uploadBatchId]);

  // Handle success/error states
  useEffect(() => {
    if (successDelete) {
      message.success(t('Subject deleted successfully'));
      handleRefresh();
      setTimeout(() => dispatch({ type: 'SUBJECT_DELETE_RESET' }), 100);
    }
  }, [successDelete, dispatch, t]);

  useEffect(() => {
    if (successUpdate && editingRowId && updatedSubject) {
      setLocalSubjects(prevSubjects => 
        prevSubjects.map(subject => 
          subject.id === updatedSubject.id ? { ...subject, ...updatedSubject } : subject
        )
      );
      message.success(t('Subject updated successfully'));
      setEditingRowId(null);
      setEditingData({});
      setTimeout(() => dispatch({ type: 'SUBJECT_UPDATE_RESET' }), 100);
    }
  }, [successUpdate, editingRowId, t, updatedSubject, dispatch]);

  useEffect(() => {
    if (errorUpdate && editingRowId) {
      message.error(Array.isArray(errorUpdate) ? errorUpdate.join(', ') : errorUpdate);
      setTimeout(() => dispatch({ type: 'SUBJECT_UPDATE_RESET' }), 100);
    }
    if (errorDelete) {
      showApiError(errorDelete, t);
      setTimeout(() => dispatch({ type: 'SUBJECT_DELETE_RESET' }), 100);
    }
  }, [errorUpdate, errorDelete, editingRowId, t, dispatch]);

  // Use backend filtering - no local filtering needed
  const filteredLocalSubjects = localSubjects;

  // Handlers
  const handleRefresh = useCallback(() => {
    if (uploadBatchId) {
      // For upload batch subjects, refresh with IDs
      dispatch(listSubjectsByIds(subjectIds));
    } else {
      // For general subject list, refresh with current filter
      dispatch(listSubjects(filter));
    }
  }, [dispatch, subjectIds, uploadBatchId, filter]);

  const handleDelete = useCallback((id) => {
    dispatch(deleteSubject(id));
  }, [dispatch]);

  const handleBulkDelete = useCallback(async () => {
    for (const key of selectedRowKeys) {
      dispatch(deleteSubject(key));
    }
    setSelectedRowKeys([]);
    message.success(t('Selected subjects deleted'));
  }, [selectedRowKeys, dispatch, t]);

  const handleEdit = useCallback((record) => {
    setEditingRowId(record.id);
    setEditingData({ ...record });
  }, []);

  const handleSave = useCallback(() => {
    if (editingRowId && editingData) {
      const currentSubject = localSubjects.find(s => s.id === editingRowId);
      const changes = {};
      Object.keys(editingData).forEach(key => {
        if (editingData[key] !== currentSubject[key]) {
          changes[key] = editingData[key];
        }
      });

      if (Object.keys(changes).length > 0) {
        dispatch(updateSubject(editingRowId, changes));
      } else {
        setEditingRowId(null);
        setEditingData({});
      }
    }
  }, [editingRowId, editingData, localSubjects, dispatch]);

  const handleCancel = useCallback(() => {
    setEditingRowId(null);
    setEditingData({});
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleViewDetails = useCallback((record) => {
    navigate(`/subjects/${record.id}/edit`);
  }, [navigate]);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setFilter(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
      sortBy: sorter.field || 'createdAt',
      sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc'
    }));
  }, []);

  const isLoading = uploadBatchId ? loadingListByIds : loadingList;
  const error = uploadBatchId ? errorListByIds : errorList;

  // Table columns
  const columns = [
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'left',
      width: 70,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        return (
          <Space size="small">
            {isEditing ? (
              <>
                <Button type="primary" size="small" icon={<FiSave />} onClick={handleSave} loading={loadingUpdate} />
                <Button size="small" icon={<FiX />} onClick={handleCancel} />
              </>
            ) : (
              <>
                <Button type="text" size="small" icon={<FiEdit />} onClick={() => handleEdit(record)} />
                <Popconfirm
                  title={t('Delete Subject')}
                  onConfirm={() => handleDelete(record.id)}
                  okText={t('Yes')}
                  cancelText={t('No')}
                >
                  <Button type="text" size="small" icon={<FiTrash2 />} danger loading={loadingDelete} />
                </Popconfirm>
              </>
            )}
          </Space>
        );
      }
    },
    {
      title: t('Subject ID'),
      dataIndex: 'subjectIdentifier',
      key: 'subjectIdentifier',
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Input
            value={editingData.subjectIdentifier || text}
            onChange={(e) => handleInputChange('subjectIdentifier', e.target.value)}
            size="small"
          />
        ) : (
          <div 
            style={{ 
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--ant-color-primary-bg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={() => handleViewDetails(record)}
            title={t('Click to view details')}
          >
            <Text style={{ fontWeight: 'bold', fontSize: '13px' }}>
              {text}
            </Text>
          </div>
        );
      }
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.status || text}
            onChange={(value) => handleInputChange('status', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="Pending">Pending</Select.Option>
            <Select.Option value="Approved">Approved</Select.Option>
            <Select.Option value="Rejected">Rejected</Select.Option>
          </Select>
        ) : (
          <Tag
            size="small"
            color={
              text === 'REJECTED' ? 'red' :
              text === 'APPROVED' ? 'green' :
              text === 'PROVISIONALLY_APPROVED' ? 'green' :
              text === 'Pending' ? 'orange' :
              'default'
            }
          >
            {text}
          </Tag>
        );
      }
    },
    {
      title: t('Postcode'),
      dataIndex: 'postcode',
      key: 'postcode',
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Input
            value={editingData.postcode || text}
            onChange={(e) => handleInputChange('postcode', e.target.value)}
            size="small"
          />
        ) : (
          <Text style={{ fontSize: '12px' }}>{text}</Text>
        );
      }
    },
    {
      title: t('Enrollment Status'),
      dataIndex: 'enrollmentStatus',
      key: 'enrollmentStatus',
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.enrollmentStatus || text}
            onChange={(value) => handleInputChange('enrollmentStatus', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="ENROLLED">Enrolled</Select.Option>
            <Select.Option value="PENDING">Pending</Select.Option>
            <Select.Option value="DECLINED">Declined</Select.Option>
          </Select>
        ) : (
          <Tag
            size="small"
            color={
              text === 'ENROLLED' ? 'green' :
              text === 'PENDING' ? 'orange' :
              text === 'DECLINED' ? 'red' :
              'default'
            }
          >
            {text}
          </Tag>
        );
      }
    },
    {
      title: t('Approval Status'),
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.approvalStatus || text}
            onChange={(value) => handleInputChange('approvalStatus', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="PROVISIONALLY_APPROVED">Provisionally Approved</Select.Option>
            <Select.Option value="REJECTED">Rejected</Select.Option>
            <Select.Option value="PENDING_REVIEW">Pending Review</Select.Option>
          </Select>
        ) : (
          <Tag
            size="small"
            color={
              text === 'REJECTED' ? 'red' :
              text === 'PROVISIONALLY_APPROVED' ? 'green' :
              text === 'APPROVED' ? 'green' :
              text === 'PENDING_REVIEW' ? 'orange' :
              'default'
            }
          >
            {text}
          </Tag>
        );
      }
    },
    {
      title: t('Study Role'),
      dataIndex: 'studyRole',
      key: 'studyRole',
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.studyRole || text}
            onChange={(value) => handleInputChange('studyRole', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="PRIMARY">Primary</Select.Option>
            <Select.Option value="CONTROL">Control</Select.Option>
            <Select.Option value="SECONDARY">Secondary</Select.Option>
          </Select>
        ) : (
          <Text style={{ fontSize: '12px' }}>{text || '-'}</Text>
        );
      }
    },
    {
      title: t('Enrollment Date'),
      dataIndex: 'enrollmentDate',
      key: 'enrollmentDate',
      render: (date) => (
        <Text style={{ fontSize: '11px' }}>
          {date ? dayjs(date).format('YYYY-MM-DD') : '-'}
        </Text>
      )
    },
    {
      title: t('Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Text style={{ fontSize: '11px' }}>
          {dayjs(date).format('YYYY-MM-DD HH:mm:ss')}
        </Text>
      )
    }
  ];

  return (
    <Drawer
      title={
        <Space>
          <Text strong>{title}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            ({uploadBatchId ? filteredLocalSubjects?.length || 0 : totalCount || 0} {t('items')})
          </Text>
        </Space>
      }
      placement="right"
      width={1200}
      onClose={onClose}
      open={visible}
      bodyStyle={{ padding: 16 }}
    >
      <ApiErrorAlert error={error} closable />

      <SubjectTableTitle
        filter={filter}
        setFilter={setFilter}
        searchText={searchText}
        setSearchText={setSearchText}
        searchProp={searchProp}
        setSearchProp={setSearchProp}
        selectedRowKeys={selectedRowKeys}
        handleBulkDelete={handleBulkDelete}
        isBulkDeleting={loadingDelete}
        onRefresh={handleRefresh}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        t={t}
        cohortFilter={cohortFilter}
        setCohortFilter={setCohortFilter}
        patientFilter={patientFilter}
        setPatientFilter={setPatientFilter}
      />

      <Table
        columns={columns}
        dataSource={filteredLocalSubjects}
        rowKey="id"
        loading={isLoading}
        size="small"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        pagination={uploadBatchId ? false : {
          current: currentPage,
          pageSize: filter.limit,
          total: totalCount,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
          pageSizeOptions: ['10', '20', '50', '100'],
          size: 'small'
        }}
        onChange={uploadBatchId ? undefined : handleTableChange}
        scroll={{ x: 1000 }}
        tableLayout="auto"
      />
    </Drawer>
  );
};

export default SubjectListByLocation; 